/**
 * PIN reset dialog. Two modes share the same component so the markup +
 * validation rules stay aligned and we only have one place to bump if
 * the BE PIN format ever changes (currently /^\d{4,8}$/, enforced
 * server-side via hashClockInPin).
 *
 *   • `self` mode    — user is resetting their own clock-in PIN. Requires
 *                      the current account password as a challenge so a
 *                      stolen JWT alone can't mint a new PIN. POSTs to
 *                      /api/users/me/pinReset.
 *   • `manager` mode — admin/manager is forcing a reset on someone else's
 *                      account. No password challenge — roleAccess is the
 *                      audit point. POSTs to /api/users/:id/pinReset.
 *
 * Caller passes:
 *   { mode: 'self' | 'manager', userId?: string, displayName?: string }
 *
 * `userId` is required in manager mode and ignored in self mode (the
 * /me endpoint pulls the caller's user id from the JWT).
 */
import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {HttpClient} from '@angular/common/http';

import {environment} from '../../../../environments/environment';

type PinResetMode = 'self' | 'manager';

interface PinResetData {
  mode: PinResetMode;
  userId?: string;
  displayName?: string;
}

@Component({
  selector: 'app-pin-reset-dialog',
  templateUrl: './pin-reset-dialog.component.html',
  styleUrls: ['./pin-reset-dialog.component.scss'],
})
export class PinResetDialogComponent {
  mode: PinResetMode;
  displayName: string;
  form: FormGroup;
  loading = false;
  errorMessage = '';

  constructor(
    private dialogRef: MatDialogRef<PinResetDialogComponent>,
    private formBuilder: FormBuilder,
    private http: HttpClient,
    @Inject(MAT_DIALOG_DATA) public data: PinResetData,
  ) {
    this.mode = data?.mode === 'manager' ? 'manager' : 'self';
    this.displayName = data?.displayName || '';
    // Self-serve carries the password challenge; manager mode skips it.
    // The validators line up with the BE: /^\d{4,8}$/ for newPin.
    const config: any = {
      newPin: ['', [Validators.required, Validators.pattern(/^\d{4,8}$/)]],
      confirmPin: ['', [Validators.required]],
    };
    if (this.mode === 'self') {
      config.currentPassword = ['', [Validators.required]];
    }
    this.form = this.formBuilder.group(config);
  }

  get title(): string {
    return this.mode === 'self' ? 'Reset your clock-in PIN' : 'Reset clock-in PIN';
  }

  get submitLabel(): string {
    return this.loading ? 'Saving…' : 'Save new PIN';
  }

  cancel(): void {
    if (!this.loading) { this.dialogRef.close(); }
  }

  submit(): void {
    this.errorMessage = '';
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const {newPin, confirmPin, currentPassword} = this.form.value;
    if (newPin !== confirmPin) {
      this.errorMessage = 'PINs do not match.';
      return;
    }
    if (this.mode === 'manager' && !this.data?.userId) {
      // Defensive — caller forgot to pass a target. Fail closed.
      this.errorMessage = 'No employee selected.';
      return;
    }
    this.loading = true;

    const url = this.mode === 'self'
      ? `${environment.apiUrl}/users/me/pinReset`
      : `${environment.apiUrl}/users/${this.data.userId}/pinReset`;
    const body: any = {newPin};
    if (this.mode === 'self') { body.currentPassword = currentPassword; }

    this.http.post<any>(url, body).subscribe(
      (response) => {
        this.loading = false;
        // Surface the BE response shape directly so callers can branch
        // on `result.success` if they care, or just check truthiness.
        this.dialogRef.close({success: !!(response && response.success !== false), mode: this.mode});
      },
      (err) => {
        this.loading = false;
        const message = err?.error?.message || err?.error || err?.message;
        this.errorMessage = typeof message === 'string' && message
          ? message
          : 'Could not reset the PIN. Try again.';
      },
    );
  }
}
